import React, { useMemo } from "react";
import { useListEnquiriesQuery, useListOrdersQuery } from "../../salesApi";

// Cascading "optional Sales Enquiry/Order number" picker for Quotation
// Create - only queries once a customer is picked (custId), filtered to
// that exact customer via the additive custId param on both list endpoints
// (Batch114). Enquiry and Order are matched by a human number string, not a
// shared RecordGuid, so an Order option carries the number the user
// actually picked (SalesReferenceNumber) alongside its own originating
// enquiry's RecordGuid (already selected by sp_app_sales_order_list as
// EnquiryRecordGuid) - that's the FK the Quotation ultimately stores.
const SalesReferencePicker = ({ customerBranchId, custId, value, onChange, disabled }) => {
  const canQuery = Boolean(customerBranchId && custId);

  const { data: enquiryData, isFetching: isFetchingEnquiries } = useListEnquiriesQuery(
    { branchId: customerBranchId, custId, pageSize: 100 },
    { skip: !canQuery }
  );
  const { data: orderData, isFetching: isFetchingOrders } = useListOrdersQuery(
    { branchId: customerBranchId, custId, view: "ALL", pageSize: 100 },
    { skip: !canQuery }
  );

  const options = useMemo(() => {
    const enquiryOptions = (enquiryData?.items || []).map((e) => ({
      key: `enquiry-${e.recordGuid}`,
      label: `${e.enquiryNumber} (Enquiry)`,
      salesEnquiryRecordGuid: e.recordGuid,
      salesReferenceNumber: e.enquiryNumber,
    }));
    // Closed and cancelled orders are excluded (2026-09-16). A closed order has
    // been invoiced and settled, so allowing a new document to be raised
    // against it re-opens something already paid for - the same control gap
    // that keeps closed orders out of the Stock-Out picker. The list is
    // fetched with view ALL and narrowed here, because the flags are what
    // actually decide it, not the view name.
    const orderOptions = (orderData?.items || [])
      .filter((o) => !o.isClosed && !o.isCancelled)
      .map((o) => ({
        key: `order-${o.recordGuid}`,
        label: `${o.orderNumber} (Order)`,
        salesEnquiryRecordGuid: o.enquiryRecordGuid,
        salesReferenceNumber: o.orderNumber,
      }));
    return [...enquiryOptions, ...orderOptions];
  }, [enquiryData, orderData]);

  if (!canQuery) {
    return (
      <select className="form-control" disabled>
        <option>Select a customer first</option>
      </select>
    );
  }

  return (
    <select
      className="form-control"
      disabled={disabled || isFetchingEnquiries || isFetchingOrders}
      value={value?.key || ""}
      onChange={(e) => {
        const selected = options.find((o) => o.key === e.target.value);
        onChange(selected || null);
      }}
    >
      <option value="">None (optional)</option>
      {options.length === 0 && !isFetchingEnquiries && !isFetchingOrders && (
        <option value="" disabled>No enquiries/orders found for this customer</option>
      )}
      {options.map((o) => (
        <option key={o.key} value={o.key}>{o.label}</option>
      ))}
    </select>
  );
};

export default SalesReferencePicker;
